import React from 'react'
import EnhancedMeta from '../components/meta/enhanced-meta'
import Layout from '../components/layout/layout'
import { graphql } from 'gatsby'
import { generateServiceSchema, generateFAQSchema, generateBreadcrumbSchema } from '../utils/schema-generator'
import Parallax from '../components/parallax'
import ServiceTable from '../components/service-table'
import GoogleMapsEmbed from '../components/google-maps-embed'
import { Treatment, Question } from '../interfeces'
import RelatedTreatments from '../components/related-treatments'

interface Props {
  data: any
  location: Location
}

const treatment: Treatment = {
  duration: '30-60 minutes',
  session: '1 session',
  results: 'Results 6-12 months',
  cost: 'From £115',
  downtime: 'Minimal',
}

const relatedTreatments = [
  {
    name: 'Russian Lips Cheshunt',
    slug: 'russian-lips',
    description:
      'Discover our advanced Russian lip technique for heart-shaped lips with elegant height.',
  },
  {
    name: 'Cheek Fillers Cheshunt',
    slug: 'cheek-fillers',
    description:
      'Balance your profile with cheek fillers that add lift and definition to the mid-face.',
  },
  {
    name: 'Dermal Fillers Cheshunt',
    slug: 'dermal-fillers-cheshunt',
    description:
      'Restore facial volume and smooth deeper lines to complement your new lip shape.',
  },
]

const questions: Array<Question> = [
  {
    question: 'How much do lip fillers cost in Cheshunt?',
    answer:
      'Lip filler treatments at Ada Aesthetics in Cheshunt start from £115 for 0.5ml and £149.99 for 1.1ml of premium Revolax filler. We also offer package deals for multiple areas. The final cost depends on the amount of product needed and your desired result. Book a free consultation to discuss your goals and get a personalized quote.',
  },
  {
    question: 'What is lip enhancement?',
    answer:
      'Lip enhancement is a cosmetic procedure which gives the patient smooth, plump, and youthful lips. At our Cheshunt clinic, we refine the overall aesthetic of the lips by smoothening vertical lip lines and bringing out the true shape. The volume of the lips is increased with carefully placed injections which add hydration. The goal is to give you the perfect lips you envision, while ensuring they look natural.',
  },
  {
    question: 'How do lip fillers work?',
    answer:
      'Lip injections fill the lips with Hyaluronic Acid to increase volume and remove lines. Because Hyaluronic Acid is a naturally occurring substance in the body, it is accepted effortlessly. Over the following 12 months, it is slowly broken down until it is gone. This makes it a temporary lip filler, which our Cheshunt practitioners consider much safer than permanent fillers such as silicon or implants.',
  },
  {
    question: 'How long do lip fillers last?',
    answer:
      'Lip fillers typically last 12 months. At Ada Aesthetics in Cheshunt, we use premium hyaluronic acid fillers that provide long-lasting, natural results. We recommend follow-up appointments to maintain your desired look.',
  },
  {
    question: 'Does it hurt?',
    answer:
      'Many people find their procedure at Ada Aesthetics in Cheshunt more comfortable than expected. Most dermal fillers contain a local anaesthetic. Your experienced Ada Aesthetics practitioner will ensure your treatment is best in class with minimal discomfort.',
  },
  {
    question: 'Can I go back to work after lip fillers?',
    answer:
      'Yes! Most of our Cheshunt clients return to work the same day. You may experience slight swelling or tenderness for 24-48 hours, but this is easily managed. We provide detailed aftercare instructions at our Cheshunt clinic.',
  },
  {
    question: 'What should I avoid after lip fillers?',
    answer:
      'For 24 hours after your treatment at our Cheshunt clinic, avoid strenuous exercise, alcohol, extreme heat (saunas, sunbeds), and touching or pressing on your lips. We provide full aftercare guidance.',
  },
  {
    question: 'Are lip fillers safe?',
    answer:
      'When administered by qualified professionals like our experienced team at Ada Aesthetics in Cheshunt, lip fillers are very safe. We use only FDA-approved, premium hyaluronic acid products and follow strict safety protocols at our Cheshunt clinic.',
  },
]

const LipFillers: React.FC<Props> = ({ data, location }: Props) => {
  const meta = data.site?.meta

  const serviceSchema = generateServiceSchema(
    'Lip Fillers in Cheshunt',
    'Professional lip enhancement treatments using hyaluronic acid fillers for natural-looking, fuller lips. Expert lip filler service in Cheshunt, Hertfordshire by qualified practitioners.',
    '200'
  )

  const faqSchema = generateFAQSchema(questions.map(q => ({
    question: q.question,
    answer: q.answer
  })))

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: 'https://adaaesthetics.uk' },
    { name: 'Treatments', url: 'https://adaaesthetics.uk/treatments' },
    { name: 'Lip Fillers Cheshunt', url: 'https://adaaesthetics.uk/lip-fillers' }
  ])

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [serviceSchema, faqSchema, breadcrumbSchema]
  }

  return (
    <Layout location={location}>
      <EnhancedMeta
        title="Lip Fillers Cheshunt - Natural Lip Enhancement | Ada Aesthetics"
        description="Expert lip filler treatments in Cheshunt. Natural-looking lip enhancement using hyaluronic acid. 12-month results, 60-minute treatment. Book consultation: 07478705252"
        keywords="lip fillers Cheshunt, lip enhancement Hertfordshire, hyaluronic acid lips, natural lip fillers, lip injections Cheshunt"
        url="https://adaaesthetics.uk/lip-fillers"
        type="service"
        schemaData={combinedSchema}
        site={meta}
      />
      <Parallax
        titleName="Natural Looking Lip Fillers"
        customClass="page_title"
      />

      <section className="ls service-single s-pt-70 s-pb-20 s-pb-sm-50 s-py-lg-100 s-pt-xl-150 s-pb-xl-100 c-gutter-60 c-mb-50">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h1 className="special-heading">Get Fuller, Beautifully Shaped Lips with Natural Lip Fillers in Cheshunt</h1>
              <p className="excerpt">
                Ready to love your smile again? At Ada Aesthetics in Cheshunt, our lip fillers use premium hyaluronic acid to create soft, natural-looking volume and definition that keeps your lips in harmony with your facial features. Treatments take around 60 minutes, results last up to 12 months, and pricing starts from £115. Serving Cheshunt, Waltham Cross, Broxbourne, and surrounding Hertfordshire areas.
              </p>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <picture>
                <source
                  srcSet="images/blog/lip-injections.webp"
                  type="image/webp"
                />
                <source
                  srcSet="images/blog/lip-injections.jpg"
                  type="image/jpeg"
                />
                <img
                  src="images/blog/lip-injections.jpg"
                  className="alignright"
                  alt="Natural lip filler treatment results at Ada Aesthetics in Cheshunt showing before and after lip enhancement"
                />
              </picture>

              <h2>Why Choose Lip Fillers in Cheshunt?</h2>
              <p>
                Lip enhancement is one of our most popular treatments at Ada Aesthetics in Cheshunt.
                Full, well-defined lips are a timeless symbol of youth and beauty, creating balanced
                facial proportions and enhancing your natural features.
              </p>
              <p>
                Lip enhancement at our Cheshunt clinic will give you natural looking, fuller lips after just
                one treatment. We make sure that your new lips are in perfect proportion with the rest of your
                face. It's time to feel more confident about your smile! For those seeking a specific lip shape,
                explore our <a href="/russian-lips">Russian lips technique</a> for beautifully defined, heart-shaped lips.
              </p>

              <h2>What Are Lip Fillers?</h2>
              <p>
                Lip fillers are injectable treatments that use hyaluronic acid - a naturally occurring substance
                in your body - to add volume, shape, and definition to your lips. At Ada Aesthetics in Cheshunt,
                we use only premium, FDA-approved fillers for the safest, most natural-looking results.
              </p>
              <p>
                Unlike permanent fillers or surgical procedures, hyaluronic acid lip fillers are temporary,
                lasting approximately 12 months. This means you can adjust your look over time and ensures
                the safest possible treatment at our Cheshunt clinic.
              </p>

              <h2>What Can Lip Fillers Achieve?</h2>
              <p>Our lip filler treatments at Ada Aesthetics in Cheshunt can:</p>
              <ul>
                <li><strong>Add volume</strong> to thin or aging lips</li>
                <li><strong>Enhance lip shape</strong> and definition</li>
                <li><strong>Smooth vertical lip lines</strong> (smoker's lines)</li>
                <li><strong>Create symmetry</strong> between upper and lower lips</li>
                <li><strong>Define the cupid's bow</strong> for a more youthful appearance</li>
                <li><strong>Hydrate and soften</strong> dry, cracked lips</li>
                <li><strong>Restore volume</strong> lost due to aging</li>
              </ul>

              <h2>Your Lip Filler Treatment Journey in Cheshunt</h2>

              <h3>1. Free Consultation</h3>
              <p>
                Every lip filler journey at Ada Aesthetics in Cheshunt begins with a thorough, free consultation.
                We'll discuss your goals, assess your facial structure, review your medical history, and create
                a personalized treatment plan. We believe in natural-looking results that enhance your features.
              </p>

              <h3>2. The Treatment</h3>
              <p>
                Your lip filler treatment takes approximately 60 minutes at our Cheshunt clinic. We use ultra-fine
                needles to precisely inject hyaluronic acid into specific areas of your lips. Most fillers contain
                local anaesthetic for your comfort, and our experienced practitioners ensure a gentle, professional
                experience.
              </p>

              <h3>3. Immediate Results</h3>
              <p>
                You'll see an immediate improvement in lip volume and shape. Some swelling is normal and typically
                subsides within 24-48 hours. Your final results will be visible within 2 weeks, revealing naturally
                plump, beautifully shaped lips.
              </p>

              <h3>4. Aftercare & Maintenance</h3>
              <p>
                Our Cheshunt team provides detailed aftercare instructions to ensure optimal results. Most clients
                return to work the same day. Results last up to 12 months, and we recommend follow-up appointments
                to maintain your desired look.
              </p>

              <h2>Why Choose Ada Aesthetics for Lip Fillers in Cheshunt?</h2>
              <ul>
                <li><strong>Qualified, experienced practitioners</strong> with extensive lip enhancement training</li>
                <li><strong>Premium hyaluronic acid products</strong> - FDA-approved and safe</li>
                <li><strong>Natural-looking results</strong> - we enhance your features, not change them</li>
                <li><strong>Free consultations</strong> to discuss your goals</li>
                <li><strong>Conveniently located</strong> in Cheshunt, Hertfordshire</li>
                <li><strong>Serving local areas</strong> including Waltham Cross, Broxbourne, Hoddesdon, and Enfield</li>
                <li><strong>Competitive pricing</strong> with transparent costs</li>
                <li><strong>Strict safety protocols</strong> and hygiene standards</li>
              </ul>

              <h2>Lip Fillers for Cheshunt Residents</h2>
              <p>
                Located at 15 Ousden Drive, Cheshunt, EN8 9RL, Ada Aesthetics is your trusted local provider
                for lip enhancement treatments. We're easily accessible for residents throughout Hertfordshire,
                including Cheshunt, Waltham Cross, Broxbourne, Hoddesdon, Waltham Abbey, and Enfield.
              </p>
              <p>
                No need to travel to London for quality lip fillers - expert aesthetic care is available right
                here in Cheshunt at competitive prices. We also offer complementary treatments including{' '}
                <a href="/cheek-fillers">cheek fillers</a>, <a href="/dermal-fillers-cheshunt">dermal fillers</a>, and{' '}
                <a href="/botox-cheshunt">Botox anti-wrinkle treatments</a> for comprehensive facial rejuvenation.
              </p>

              <h2>Book Your Lip Filler Consultation in Cheshunt</h2>
              <p>
                Ready for fuller, more defined lips? Join 500+ satisfied clients who love their natural lip results.
                Schedule your free consultation today - our experienced team will answer all your questions and create a personalized
                treatment plan to help you achieve your perfect pout.
              </p>
              <p>
                <strong>Book today to secure your preferred time</strong> - Call us at <strong>07478705252</strong> or reserve your appointment online.
                Your complimentary consultation (worth £50) includes lip assessment and personalized treatment plan.
              </p>

              <RelatedTreatments treatments={relatedTreatments} />

              <GoogleMapsEmbed
                embedUrl="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2469.9642841486544!2d-0.04449842346989817!3d51.70251797183743!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48761f5e6c8f7d0d%3A0x9f3e8f3e8f3e8f3e!2s15%20Ousden%20Drive%2C%20Cheshunt%2C%20Waltham%20Cross%20EN8%209RL!5e0!3m2!1sen!2suk!4v1234567890123"
                height={450}
                title="Ada Aesthetics Lip Fillers Clinic Location in Cheshunt"
                loading="lazy"
              />
            </div>
          </div>

          <ServiceTable treatment={treatment} questions={questions} />
        </div>
      </section>
    </Layout>
  )
}

export default LipFillers

export const pageQuery = graphql`
  query LipFillers {
    site {
      meta: siteMetadata {
        title
        description
        siteUrl
        author
      }
    }
  }
`
